import React, { useEffect, useRef, useState } from 'react';
import { normalizeAssetId } from './trackingModel';
import {
  addNativeGearScanListener,
  nativeNfcAvailable,
  scanNativeGearTags,
  setNativeGearScanFeedback,
} from '../native/nativeNfc';

const SAME_CODE_COOLDOWN_MS = 2500;

function postScannerMessage(message) {
  if (!('serviceWorker' in navigator)) return;
  if (navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage(message);
    return;
  }
  navigator.serviceWorker.ready
    .then(registration => registration.active?.postMessage(message))
    .catch(() => {});
}

function cameraErrorMessage(err) {
  const name = String(err?.name || '');
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    return 'Camera access was denied. Allow Camera for this site/app in iPhone Settings or Safari website settings, then try again.';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return 'No usable camera was found on this device.';
  return err?.message || 'Unable to access the camera.';
}

function waitForVideo(ref) {
  return new Promise(resolve => {
    const check = () => {
      if (ref.current) resolve(ref.current);
      else requestAnimationFrame(check);
    };
    requestAnimationFrame(check);
  });
}

function normalizeFeedback(result, assetId) {
  const status = ['added', 'duplicate', 'error'].includes(result?.status) ? result.status : 'added';
  const fallback = status === 'added'
    ? `${assetId} added. Keep scanning.`
    : status === 'duplicate'
      ? `${assetId} is already in this scan.`
      : `${assetId} could not be added.`;

  return {
    status,
    message: String(result?.message || fallback),
  };
}

function feedbackStyle(status) {
  if (status === 'error') {
    return { background: '#fef2f2', border: '#fecaca', text: '#991b1b', icon: '✕' };
  }
  if (status === 'duplicate') {
    return { background: '#fffbeb', border: '#fde68a', text: '#92400e', icon: '•' };
  }
  return { background: '#ecfdf5', border: '#a7f3d0', text: '#166534', icon: '✓' };
}

export default function QrScanner({ onScan, label = 'Scan gear', selectedCount = 0 }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const frameRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const nfcReaderRef = useRef(null);
  const onScanRef = useRef(onScan);
  const feedbackTimerRef = useRef(null);
  const recentReadRef = useRef({ id: '', at: 0 });
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('');
  const [manual, setManual] = useState('');
  const [error, setError] = useState('');
  const [cameraEngine, setCameraEngine] = useState('');
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const showFeedback = next => {
    setFeedback(next);
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = window.setTimeout(() => setFeedback(null), 1700);
  };

  const handleDetection = async (value, { force = false } = {}) => {
    const id = normalizeAssetId(value);
    if (!id) return null;

    const now = Date.now();
    const recent = recentReadRef.current;
    if (!force && recent.id === id && now - recent.at < SAME_CODE_COOLDOWN_MS) return null;
    recentReadRef.current = { id, at: now };

    setError('');
    setManual('');

    try {
      const result = await Promise.resolve(onScanRef.current?.(id));
      const normalized = normalizeFeedback(result, id);
      showFeedback(normalized);
      return normalized;
    } catch (err) {
      const normalized = normalizeFeedback({
        status: 'error',
        message: err?.message || `${id} could not be added.`,
      }, id);
      showFeedback(normalized);
      return normalized;
    }
  };

  const stopCamera = () => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    if (zxingControlsRef.current) {
      try { zxingControlsRef.current.stop(); } catch (_) {}
      zxingControlsRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setOpen(false);
    setCameraEngine('');
  };

  const close = () => {
    stopCamera();
    if (nfcReaderRef.current) {
      try { nfcReaderRef.current.abort?.(); } catch (_) {}
      nfcReaderRef.current = null;
    }
    setMode('');
    setError('');
  };

  useEffect(() => () => {
    stopCamera();
    if (feedbackTimerRef.current) window.clearTimeout(feedbackTimerRef.current);
    if (nfcReaderRef.current) {
      try { nfcReaderRef.current.abort?.(); } catch (_) {}
    }
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;

    const announceActive = () => postScannerMessage({ type: 'SMFD_SCANNER_ACTIVE' });
    const handleControllerChange = () => announceActive();
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') announceActive();
    };
    const handleServiceWorkerMessage = event => {
      const type = event.data?.type;
      if (type === 'SMFD_SCANNER_PROBE') {
        announceActive();
        return;
      }
      if (type !== 'SMFD_ASSET_TAG_SCAN') return;

      const id = normalizeAssetId(event.data?.assetId);
      if (!id) return;
      void handleDetection(id, { force: true });
    };

    announceActive();
    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      postScannerMessage({ type: 'SMFD_SCANNER_INACTIVE' });
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const startNativeQrCamera = async video => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false,
    });
    streamRef.current = stream;
    video.srcObject = stream;
    await video.play();
    const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
    setCameraEngine('native');

    const scanFrame = async () => {
      try {
        if (videoRef.current?.readyState >= 2) {
          const codes = await detector.detect(videoRef.current);
          if (codes?.[0]?.rawValue) await handleDetection(codes[0].rawValue);
        }
      } catch (_) {
        // Continue scanning; focus/motion can cause harmless transient detector errors.
      }
      frameRef.current = requestAnimationFrame(scanFrame);
    };
    frameRef.current = requestAnimationFrame(scanFrame);
  };

  const startZxingQrCamera = async video => {
    const { BrowserQRCodeReader } = await import('@zxing/browser');
    const reader = new BrowserQRCodeReader();
    setCameraEngine('zxing');
    zxingControlsRef.current = await reader.decodeFromConstraints(
      { video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false },
      video,
      result => {
        const value = result?.getText?.() || result?.text || '';
        if (value) void handleDetection(value);
      },
    );
  };

  const startCamera = async () => {
    stopCamera();
    setMode('camera');
    setError('');
    setFeedback(null);
    setOpen(true);

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera access is not available in this browser. Type the asset ID below instead.');
      return;
    }

    try {
      const video = await waitForVideo(videoRef);
      if ('BarcodeDetector' in window) await startNativeQrCamera(video);
      else await startZxingQrCamera(video);
    } catch (err) {
      stopCamera();
      setMode('camera');
      setOpen(true);
      setError(cameraErrorMessage(err));
    }
  };

  const startNfc = async () => {
    stopCamera();
    setMode('nfc');
    setError('');
    setFeedback(null);
    postScannerMessage({ type: 'SMFD_SCANNER_ACTIVE' });

    // Native iPhone app: keep one Core NFC session open and stream each tag into Deploy / Return.
    if (nativeNfcAvailable()) {
      let listenerHandle = null;
      const streamedIds = new Set();

      try {
        listenerHandle = await addNativeGearScanListener(async assetId => {
          const id = normalizeAssetId(assetId);
          if (!id) return;

          streamedIds.add(id);
          const result = await handleDetection(id, { force: true });
          if (!result) return;

          try {
            await setNativeGearScanFeedback({
              assetId: id,
              status: result.status,
              message: result.message,
            });
          } catch (_) {
            // The React confirmation/list is still authoritative if native feedback cannot update.
          }
        });

        const ids = await scanNativeGearTags({ allowMultiple: true });

        // Fallback for any native IDs that arrived without an event before the session closed.
        for (const rawId of ids || []) {
          const id = normalizeAssetId(rawId);
          if (!id || streamedIds.has(id)) continue;
          await handleDetection(id, { force: true });
        }

        if (!ids?.length && !streamedIds.size) {
          setError('No Line Medic gear tag was detected.');
        }
      } catch (err) {
        const message = err?.message || 'NFC reader could not be started.';
        if (!/cancel/i.test(message)) setError(message);
      } finally {
        try { await listenerHandle?.remove?.(); } catch (_) {}
        setMode('');
      }
      return;
    }

    if ('NDEFReader' in window) {
      try {
        const reader = new window.NDEFReader();
        nfcReaderRef.current = reader;
        await reader.scan();
        reader.addEventListener('reading', event => {
          for (const record of event.message?.records || []) {
            if (record.recordType !== 'url' && record.recordType !== 'text' && record.recordType !== 'absolute-url') continue;
            try {
              const decoder = new TextDecoder(record.encoding || 'utf-8');
              const value = record.recordType === 'absolute-url' ? record.data : decoder.decode(record.data);
              const id = normalizeAssetId(value);
              if (id) {
                void handleDetection(id, { force: true });
                return;
              }
            } catch (_) {}
          }
        });
      } catch (err) {
        setError(err?.message || 'NFC reader could not be started.');
      }
    }
  };

  const feedbackTheme = feedback ? feedbackStyle(feedback.status) : null;
  const feedbackBanner = feedback && feedbackTheme ? (
    <div
      role="status"
      aria-live="polite"
      style={{
        marginTop: 10,
        padding: '11px 13px',
        borderRadius: 10,
        border: `1px solid ${feedbackTheme.border}`,
        background: feedbackTheme.background,
        color: feedbackTheme.text,
        fontSize: 14,
        fontWeight: 700,
        lineHeight: 1.35,
      }}
    >
      <span style={{ marginRight: 7 }}>{feedbackTheme.icon}</span>
      {feedback.message}
    </div>
  ) : null;

  return (
    <div className="tracking-scanner">
      <div className="tracking-scanner-methods" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
        <button type="button" className={`tracking-btn ${mode === 'camera' ? 'tracking-btn-primary' : 'tracking-btn-secondary'}`} onClick={mode === 'camera' ? close : startCamera}>
          {mode === 'camera' ? 'Done Scanning' : 'Camera QR'}
        </button>
        <button type="button" className={`tracking-btn ${mode === 'nfc' ? 'tracking-btn-primary' : 'tracking-btn-secondary'}`} onClick={mode === 'nfc' ? close : startNfc}>
          {mode === 'nfc' ? 'NFC Active' : 'NFC Tap'}
        </button>
      </div>

      <div style={{ marginTop: 8, fontSize: 13, opacity: .72 }}>{label}</div>
      <div style={{ marginTop: 5, fontSize: 13, fontWeight: 700 }}>
        {selectedCount} {selectedCount === 1 ? 'item' : 'items'} scanned
      </div>

      {mode === 'nfc' && (
        <div className="tracking-scanner-panel" style={{ marginTop: 10 }}>
          <div className="tracking-identity-note">
            <strong>NFC ready.</strong>{' '}
            {nativeNfcAvailable()
              ? 'Keep the Apple NFC reader open and tap each gear tag one at a time. Each accepted item is added immediately. Apple labels the finish button Cancel — tap it when you are done.'
              : 'Hold the phone near each gear tag. Each accepted item is added immediately and the scanner stays ready for the next tag.'}
          </div>
          {feedbackBanner}
          {error && <div className="tracking-warning">{error}</div>}
        </div>
      )}

      {mode === 'camera' && open && (
        <div className="tracking-scanner-panel">
          {!error && (
            <div style={{ position: 'relative' }}>
              <video ref={videoRef} className="tracking-scanner-video" playsInline muted />
              {feedback && feedbackTheme && (
                <div
                  role="status"
                  aria-live="polite"
                  style={{
                    position: 'absolute',
                    left: 12,
                    right: 12,
                    bottom: 12,
                    padding: '11px 13px',
                    borderRadius: 10,
                    border: `1px solid ${feedbackTheme.border}`,
                    background: feedbackTheme.background,
                    color: feedbackTheme.text,
                    fontSize: 14,
                    fontWeight: 800,
                    lineHeight: 1.35,
                    boxShadow: '0 5px 16px rgba(0,0,0,.18)',
                  }}
                >
                  <span style={{ marginRight: 7 }}>{feedbackTheme.icon}</span>
                  {feedback.message}
                </div>
              )}
            </div>
          )}
          {cameraEngine === 'zxing' && !error && <div style={{ fontSize: 12, opacity: .68, marginTop: 6 }}>iPhone-compatible camera decoder active</div>}
          {error && <div className="tracking-warning">{error}</div>}
          <div style={{ marginTop: 8, fontSize: 12, opacity: .72 }}>Camera stays open after each scan. Point it at the next gear QR code when ready.</div>
          <div className="tracking-manual-row">
            <input
              value={manual}
              onChange={event => setManual(event.target.value)}
              placeholder="Or type asset ID (LM-PACK-01)"
              autoCapitalize="characters"
            />
            <button type="button" className="tracking-btn" onClick={() => void handleDetection(manual, { force: true })} disabled={!manual.trim()}>
              Add
            </button>
          </div>
        </div>
      )}

      {mode !== 'camera' && mode !== 'nfc' && feedbackBanner}
    </div>
  );
}
